import { z } from 'zod';
import {
  CUSTOMER_SERVICE_SEGMENTS,
  OCCUPANCY_STATUSES,
  PROPERTY_TYPES,
  SMS_QUOTE_NOTE_MAX,
} from '../constants/index.ts';
import { idSchema, optionalEmail, optionalText } from './common.ts';
import { emailFormat } from './formats.ts';

const countryCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z]{2}$/, 'Use a two-letter country code')
  .transform((value) => value.toUpperCase());

const customerFields = z.object({
  defaultTeamId: idSchema.nullable().optional(),
  teamFlexible: z.boolean().optional(),
  firstName: optionalText(80),
  lastName: optionalText(80),
  companyName: optionalText(160),
  email: optionalEmail,
  phone: optionalText(40),
  billingAddressLine1: optionalText(160),
  billingAddressLine2: optionalText(160),
  billingCity: optionalText(80),
  billingState: optionalText(80),
  billingPostalCode: optionalText(20),
  billingCountryCode: countryCodeSchema.nullable().optional(),
  internalNotes: optionalText(4000),
  /**
   * Payment terms — days from an invoice's issue to its due date, Net 30
   * being 30. Null (or absent) is due on receipt, the residential answer.
   * Snapshotted onto each invoice at issue, so an edit here never restates a
   * bill already out the door.
   */
  invoiceTermsDays: z.number().int().min(0).max(365).nullable().optional(),
  /** The client's own purchase-order number, printed on their invoices. */
  invoicePoNumber: optionalText(80),
  /** One invoice a month for the month's completed visits, instead of one per visit. */
  consolidatedBilling: z.boolean().optional(),
  /** Explicit consent to receive promotional text messages. Off by default. */
  smsMarketingEnabled: z.boolean().optional(),
  /** Explicit consent to receive promotional email. Off by default. */
  emailMarketingEnabled: z.boolean().optional(),
  /**
   * Text-reminder consent, as a single box the office ticks.
   *
   * Deliberately a boolean rather than the stored three-state status: an
   * estimator is recording "the customer said yes" or "the customer said no",
   * and the third state — nobody has asked — is the absence of this field, not
   * a value anyone chooses. The route turns it into the stored status and pins
   * it to the number it was given for.
   */
  smsOptIn: z.boolean().optional(),
  /**
   * Whether this customer wants a text and an email when an earlier time
   * opens up. Off until they say yes.
   */
  wantsEarlierTimes: z.boolean().optional(),
  /**
   * Home vs business for this customer. Residential (home) is the default;
   * commercial is offices, stores and similar. Tax treatment is not driven by
   * this field yet.
   */
  serviceSegment: z.enum(CUSTOMER_SERVICE_SEGMENTS).optional(),
});

export interface CustomerNameFields {
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  companyName?: string | null | undefined;
}

/** A customer with no name at all can never be found again. */
export function hasCustomerName(value: CustomerNameFields): boolean {
  return Boolean(value.firstName || value.lastName || value.companyName);
}

export const createCustomerSchema = customerFields
  .extend({
    /**
     * "The place we clean is the place we bill" — the usual case, so the form
     * offers it ticked and the customer arrives with their first property
     * already on file.
     *
     * Create-only, and deliberately not part of `customerFields`: on an update
     * the customer's properties are already a visible list of their own, and a
     * flag that quietly appended another copy of the billing address every time
     * a phone number was corrected would be a duplicate factory.
     */
    addBillingAddressAsProperty: z.boolean().optional(),
  })
  .refine(hasCustomerName, {
    message: 'Enter a first name, last name or company name',
    path: ['lastName'],
  });

/**
 * A PATCH may touch a single field, so the name rule cannot live on the
 * request — it is an invariant of the *stored* customer, checked after the
 * change is merged in. Aliasing the create schema here would force every
 * phone-number edit to resend a name.
 */
export const updateCustomerSchema = customerFields.partial();

/**
 * Emailing the customer their portal link.
 *
 * Both fields mirror `sendQuoteSchema`, for the same reasons: the address is
 * optional because the customer's own is the default, and overridable because
 * the person who answers the phone is not always the one on the record; the
 * note is what the sender wants said above the link.
 */
export const emailPortalLinkSchema = z.object({
  recipientEmail: z.email(emailFormat('Enter a valid email address')).max(320).optional(),
  message: optionalText(4000),
});

/**
 * The same send, by text.
 *
 * Mirrors `textQuoteSchema` rather than the email schema above it, and for the
 * same reason: the note is capped two orders of magnitude shorter because a
 * text is billed by the segment, and what actually decides how much of it goes
 * out is the segment cap applied to the assembled message, not this number.
 */
export const textPortalLinkSchema = z.object({
  recipientPhone: optionalText(40),
  message: optionalText(SMS_QUOTE_NOTE_MAX),
});

/**
 * How many rows one import request carries.
 *
 * Each row is its own insert and, before that, its own duplicate check, so a
 * request holding somebody's entire five-thousand-customer migration is a
 * request that will not finish. The browser splits the file into batches of
 * this size and reports progress across them, which also means a network blip
 * costs one batch rather than the afternoon.
 */
export const CUSTOMER_IMPORT_BATCH_MAX = 100;

/** What to do with a row whose customer is already on file. */
export const IMPORT_DUPLICATE_STRATEGIES = ['skip', 'update', 'create'] as const;

export type ImportDuplicateStrategy = (typeof IMPORT_DUPLICATE_STRATEGIES)[number];

/**
 * One batch of an import.
 *
 * The rows arrive already mapped to customer fields — the browser did that,
 * against the shared column table, and showed the result for correction before
 * anything was sent. What it did not do is validate them in a way anybody
 * should trust, so every row is run through `createCustomerSchema` here
 * regardless; `values` is deliberately untyped until that happens.
 */
export const importCustomersSchema = z.object({
  rows: z
    .array(
      z.object({
        /**
         * Which line of the person's own file this came from, carried through
         * so the result names the row they can actually go and look at rather
         * than a position in a batch they never saw.
         */
        // Excel's own last row. A sparse worksheet can genuinely put a
        // customer down there, and the reader now preserves that number, so a
        // lower cap would fail the whole batch over one valid row.
        line: z.number().int().min(1).max(1_048_576),
        /**
         * Set when the file carried an ID column — a list exported from here,
         * edited, and sent back. The row then updates that customer instead of
         * being matched against the contact details of everyone on file.
         */
        id: idSchema.nullable().optional(),
        values: z.record(z.string(), z.unknown()),
      }),
    )
    .min(1, 'Nothing to import')
    .max(CUSTOMER_IMPORT_BATCH_MAX, `Send at most ${CUSTOMER_IMPORT_BATCH_MAX} rows at a time`),
  duplicateStrategy: z.enum(IMPORT_DUPLICATE_STRATEGIES).default('skip'),
  /** The create form's shortcut, applied to every row this batch creates. */
  addBillingAddressAsProperty: z.boolean().optional(),
  /**
   * Check everything and write nothing.
   *
   * This is what the review step runs on: the browser can tell a row is
   * malformed by itself, but only the server knows which of these people are
   * already customers, and "34 new, 12 already on file" is the fact somebody
   * wants before they commit, not after.
   */
  dryRun: z.boolean().optional(),
});

export type ImportCustomersInput = z.infer<typeof importCustomersSchema>;

export const createPropertySchema = z.object({
  name: optionalText(120),
  propertyType: z.enum(PROPERTY_TYPES),
  addressLine1: optionalText(160),
  addressLine2: optionalText(160),
  city: optionalText(80),
  state: optionalText(80),
  postalCode: optionalText(20),
  countryCode: countryCodeSchema.default('US'),
  squareFeet: z.number().int().min(0).max(1_000_000).nullable().optional(),
  /** Optional rate per square foot for this property; blank uses the company default. */
  areaRateCents: z.number().int().min(1).max(100_000_000).nullable().optional(),
  bedrooms: z.number().min(0).max(100).nullable().optional(),
  bathrooms: z.number().min(0).max(100).nullable().optional(),
  floors: z.number().int().min(0).max(50).nullable().optional(),
  occupancyStatus: z.enum(OCCUPANCY_STATUSES).nullable().optional(),
  /**
   * Where the property is, for the clock-in distance check. Sent by the form
   * only when the address was picked from an autocomplete suggestion that
   * carried coordinates — never typed by hand, so there is nothing here to
   * validate beyond the shape of the planet.
   */
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  petsDescription: optionalText(500),
  accessNotes: optionalText(2000),
  parkingNotes: optionalText(2000),
  internalNotes: optionalText(4000),
  /**
   * The client's own reference for this building — "BR-114", "Store 0392".
   * Free text because it is THEIR filing system, not ours.
   */
  siteCode: optionalText(60),
  /**
   * The person at this building, who on a multi-site account is not the
   * person who pays the invoice. Visit notices for this site go here when it
   * is set; the customer's own address is the fallback, and is what every
   * property that exists today uses.
   */
  siteContactName: optionalText(160),
  siteContactTitle: optionalText(120),
  siteContactEmail: optionalEmail,
  siteContactPhone: optionalText(40),
});

// No default country on an edit: a partial PATCH that leaves the country out
// must not reset a stored non-US country (or read as an address change).
export const updatePropertySchema = createPropertySchema.extend({ countryCode: countryCodeSchema }).partial();

export type CreateCustomerInput = z.infer<typeof createCustomerSchema>;
export type CreatePropertyInput = z.infer<typeof createPropertySchema>;
export type UpdatePropertyInput = z.infer<typeof updatePropertySchema>;
